import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import apiClient from "../../services/api";

interface Props {
  open: boolean;
  purchaseOrderID: number;
  clientID: number;
  fromInventory?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Patient {
  patientID: number;
  firstname: string;
  lastname: string;
  email?: string;
  patientExternalID?: string;
  glTestRate?: string;
  diabetic?: boolean;
}

interface Charge {
  id: number;
  name: string;
  description: string;
  charge: number;
}

interface LineItem {
  chargeID: number;
  chargeName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

// Bundle definitions
interface BundleMultipliers {
  [key: number]: (qty: number, testRate: number) => number;
}

const BUNDLES = {
  GLUCOSE_KIT: {
    id: 90001,
    name: "Test n Share Glucose Kit",
    chargeIDs: [1, 10, 13, 14, 15],
    quantityMultipliers: {
      10: (qty: number, testRate: number) => qty * testRate * 2, // Strips - 60 days
      13: (qty: number, testRate: number) => qty * testRate, // Lancets - 30 days
    } as BundleMultipliers,
  },
  RESUPPLY_KIT: {
    id: 90002,
    name: "Resupply Kit",
    chargeIDs: [10, 13, 15],
    quantityMultipliers: {
      10: (qty: number, testRate: number) => qty * testRate * 2,
      13: (qty: number, testRate: number) => qty * testRate,
    } as BundleMultipliers,
  },
};

// Allowed charge IDs for purchase orders
const ALLOWED_CHARGE_IDS = [
  1, // Test n Share Glucose Meter (Bluetooth)
  7, // Blood Pressure Monitor (Bluetooth)
  10, // Test n Share Strips (50/box)
  13, // Lancets 30g (100/box)
  14, // Lancet Device
  15, // Control Solution
  19, // Pulse Oximeter (Bluetooth)
  21, // Weight Scale (Bluetooth)
  23, // Ear Thermometer (Bluetooth)
  26, // XL Blood Pressure Cuff ONLY
  34, // Smart Vital Watch (Bluetooth)
  1029, // Tenovi Router
  1031, // Smart Vital Watch - Loop Ring
  1032, // Smart Vital Watch - Charger
  1033, // Smart Vital Watch - Wristband
  90001, // Test n Share Glucose Kit (bundle)
  90002, // Resupply Kit (bundle)
];

const AddLineItemModal: React.FC<Props> = ({
  open,
  purchaseOrderID,
  clientID,
  fromInventory = false,
  onClose,
  onSuccess,
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [patients, setPatients] = useState<Patient[]>([]);
  const [patientSearch, setPatientSearch] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  const [charges, setCharges] = useState<Charge[]>([]);
  const [selectedChargeID, setSelectedChargeID] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(1);
  const [unitPrice, setUnitPrice] = useState<number>(0);

  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  const loadCharges = useCallback(async () => {
    try {
      const response = await apiClient.get(
        `/purchase-orders/charges/client/${clientID}`,
      );
      // Filter to only allowed charges
      const filteredCharges = response.data.charges.filter((charge: Charge) =>
        ALLOWED_CHARGE_IDS.includes(charge.id),
      );
      setCharges(filteredCharges);
    } catch (err) {
      console.error("Failed to load charges:", err);
    }
  }, [clientID]);

  useEffect(() => {
    if (open && clientID) {
      loadCharges();
    }
  }, [open, clientID, loadCharges]);

  const searchPatients = async (search: string) => {
    if (!clientID || search.length < 2) {
      setPatients([]);
      return;
    }

    try {
      const response = await apiClient.get(
        `/purchase-orders/patients/client/${clientID}`,
        { params: { search } },
      );

      // Get patient details including glTestRate
      const patientsWithDetails = await Promise.all(
        response.data.patients.map(async (p: Patient) => {
          try {
            const detailsResp = await apiClient.get(`/patients/${p.patientID}`);
            return {
              ...p,
              glTestRate: detailsResp.data.glTestRate || "0|D",
              diabetic: detailsResp.data.diabetic || false,
            };
          } catch {
            return { ...p, glTestRate: "0|D", diabetic: false };
          }
        }),
      );

      setPatients(patientsWithDetails);
    } catch (err) {
      console.error("Failed to search patients:", err);
    }
  };

  const getGlucoseTestRate = (): number => {
    if (!selectedPatient?.glTestRate) return 3;

    const parts = selectedPatient.glTestRate.split("|");
    const rate = parseInt(parts[0]);

    // If diabetic and no rate set, default to 3
    if (selectedPatient.diabetic && (isNaN(rate) || rate === 0)) {
      return 3;
    }

    return isNaN(rate) ? 3 : rate;
  };

  const handleChargeChange = (chargeID: number) => {
    setSelectedChargeID(chargeID);

    // Check if it's a bundle
    if (
      chargeID === BUNDLES.GLUCOSE_KIT.id ||
      chargeID === BUNDLES.RESUPPLY_KIT.id
    ) {
      setUnitPrice(0);
      return;
    }

    // Find the charge and set unit price
    const charge = charges.find((c) => c.id === chargeID);
    if (charge) {
      setUnitPrice(charge.charge);
    }
  };

  const handleAddToList = () => {
    if (!selectedPatient) {
      setError("Please select a patient");
      return;
    }

    if (selectedChargeID === 0) {
      setError("Please select a charge");
      return;
    }

    const testRate = getGlucoseTestRate();
    const newItems: LineItem[] = [];

    // Check if it's a bundle
    if (selectedChargeID === BUNDLES.GLUCOSE_KIT.id) {
      const bundle = BUNDLES.GLUCOSE_KIT;
      bundle.chargeIDs.forEach((chargeID) => {
        const charge = charges.find((c) => c.id === chargeID);
        if (charge) {
          const multiplier = bundle.quantityMultipliers[chargeID];
          const itemQty = multiplier
            ? multiplier(quantity, testRate)
            : quantity;

          newItems.push({
            chargeID: charge.id,
            chargeName: charge.name,
            quantity: itemQty,
            unitPrice: charge.charge,
            totalPrice: itemQty * charge.charge,
          });
        }
      });
    } else if (selectedChargeID === BUNDLES.RESUPPLY_KIT.id) {
      const bundle = BUNDLES.RESUPPLY_KIT;
      bundle.chargeIDs.forEach((chargeID) => {
        const charge = charges.find((c) => c.id === chargeID);
        if (charge) {
          const multiplier = bundle.quantityMultipliers[chargeID];
          const itemQty = multiplier
            ? multiplier(quantity, testRate)
            : quantity;

          // Use $0 prices if fromInventory is true
          const unitPrice = fromInventory ? 0 : charge.charge;
          const totalPrice = fromInventory ? 0 : itemQty * charge.charge;

          newItems.push({
            chargeID: charge.id,
            chargeName: charge.name,
            quantity: itemQty,
            unitPrice: unitPrice,
            totalPrice: totalPrice,
          });
        }
      });
    } else {
      // Single charge
      const charge = charges.find((c) => c.id === selectedChargeID);
      if (charge) {
        // Use $0 prices if fromInventory is true
        const itemUnitPrice = fromInventory ? 0 : unitPrice;
        const itemTotalPrice = fromInventory ? 0 : quantity * unitPrice;

        newItems.push({
          chargeID: charge.id,
          chargeName: charge.name,
          quantity,
          unitPrice: itemUnitPrice,
          totalPrice: itemTotalPrice,
        });
      }
    }

    setLineItems([...lineItems, ...newItems]);
    setSelectedChargeID(0);
    setQuantity(1);
    setUnitPrice(0);
    setError("");
  };

  const handleRemoveItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!selectedPatient) {
      setError("Please select a patient");
      return;
    }

    if (lineItems.length === 0) {
      setError("Please add at least one item");
      return;
    }

    setSaving(true);
    setError("");

    try {
      // Add all line items
      for (const item of lineItems) {
        await apiClient.post(`/purchase-orders/${purchaseOrderID}/details`, {
          chargeID: item.chargeID,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          totalPrice: item.totalPrice,
          patientID: selectedPatient.patientID,
          patientExternalID: selectedPatient.patientExternalID || "",
        });
      }

      handleClose();
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to add line items");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedPatient(null);
    setPatientSearch("");
    setPatients([]);
    setSelectedChargeID(0);
    setQuantity(1);
    setUnitPrice(0);
    setLineItems([]);
    setError("");
    onClose();
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle>Add Line Items</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Patient Search */}
        <Box sx={{ mb: 3 }}>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            1. Select Patient
          </Typography>
          <TextField
            fullWidth
            label='Search Patient'
            value={patientSearch}
            onChange={(e) => {
              setPatientSearch(e.target.value);
              searchPatients(e.target.value);
            }}
            disabled={saving || !!selectedPatient}
            placeholder='Search by name or external ID...'
            helperText={
              selectedPatient
                ? `Selected: ${selectedPatient.firstname} ${selectedPatient.lastname}${selectedPatient.patientExternalID ? ` (${selectedPatient.patientExternalID})` : ""} - Glucose Test Rate: ${getGlucoseTestRate()}x daily`
                : "Type to search for a patient"
            }
          />
          {patients.length > 0 && !selectedPatient && (
            <Paper
              sx={{
                position: "absolute",
                zIndex: 1000,
                maxHeight: 200,
                overflowY: "auto",
                mt: 1,
                width: "calc(100% - 48px)",
              }}
            >
              {patients.map((patient) => (
                <Box
                  key={patient.patientID}
                  sx={{
                    p: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  onClick={() => {
                    setSelectedPatient(patient);
                    setPatientSearch(
                      `${patient.firstname} ${patient.lastname}`,
                    );
                    setPatients([]);
                  }}
                >
                  <Typography variant='body2'>
                    {patient.firstname} {patient.lastname}
                    {patient.patientExternalID &&
                      ` (${patient.patientExternalID})`}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}
          {selectedPatient && (
            <Button
              variant='outlined'
              onClick={() => {
                setSelectedPatient(null);
                setPatientSearch("");
                setLineItems([]);
              }}
              disabled={saving}
              sx={{ mt: 1 }}
            >
              Clear Patient
            </Button>
          )}
        </Box>

        {/* Charge Selection */}
        {selectedPatient && (
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              2. Add Charges
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Charge</InputLabel>
                <Select
                  value={selectedChargeID || ""}
                  label='Charge'
                  onChange={(e) => handleChargeChange(Number(e.target.value))}
                  disabled={saving}
                >
                  <MenuItem value=''>
                    <em>Select a charge</em>
                  </MenuItem>
                  <MenuItem value={BUNDLES.GLUCOSE_KIT.id}>
                    {BUNDLES.GLUCOSE_KIT.name}
                  </MenuItem>
                  <MenuItem value={BUNDLES.RESUPPLY_KIT.id}>
                    {BUNDLES.RESUPPLY_KIT.name}
                  </MenuItem>
                  {charges?.map((charge) => (
                    <MenuItem key={charge.id} value={charge.id}>
                      {charge.name} - {charge.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label='Quantity'
                type='number'
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                disabled={saving}
                inputProps={{ min: 1 }}
                sx={{ width: 150 }}
              />
              {selectedChargeID > 0 &&
                selectedChargeID !== BUNDLES.GLUCOSE_KIT.id &&
                selectedChargeID !== BUNDLES.RESUPPLY_KIT.id && (
                  <TextField
                    label='Unit Price'
                    type='number'
                    value={unitPrice}
                    onChange={(e) => setUnitPrice(Number(e.target.value))}
                    disabled={saving}
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ width: 150 }}
                  />
                )}
              <Button
                variant='contained'
                onClick={handleAddToList}
                disabled={saving || selectedChargeID === 0}
              >
                Add
              </Button>
            </Box>
          </Box>
        )}

        {/* Line Items List */}
        {lineItems.length > 0 && (
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              3. Review Items
            </Typography>
            <TableContainer component={Paper} variant='outlined'>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Charge</TableCell>
                    <TableCell align='right'>Quantity</TableCell>
                    <TableCell align='right'>Unit Price</TableCell>
                    <TableCell align='right'>Total</TableCell>
                    <TableCell align='center' sx={{ width: 50 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.chargeName}</TableCell>
                      <TableCell align='right'>{item.quantity}</TableCell>
                      <TableCell align='right'>
                        ${item.unitPrice.toFixed(2)}
                      </TableCell>
                      <TableCell align='right'>
                        ${item.totalPrice.toFixed(2)}
                      </TableCell>
                      <TableCell align='center'>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() => handleRemoveItem(index)}
                          disabled={saving}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} align='right'>
                      <strong>Total:</strong>
                    </TableCell>
                    <TableCell align='right'>
                      <strong>${calculateTotal().toFixed(2)}</strong>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={saving || lineItems.length === 0}
          startIcon={saving && <CircularProgress size={20} />}
        >
          {saving ? "Saving..." : "Apply to PO"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLineItemModal;
